import React, { useRef, useEffect, useCallback, useState } from 'react'
import ReactDOM from 'react-dom'
import classNames from 'classnames'
import { Sheet } from 'react-modal-sheet'
import ActionSheetLayout from '@/components/action-sheet-layout'
import useIsMobileSize from '@/hooks/useIsMobileSize'
import { DEFAULT_SHEET_TWEEN, SHEET_CLOSE_EASE, getSheetExitMs } from './sheetTiming'
import './Modal.scss'

const getModalRoot = () => (typeof document !== 'undefined' ? document.getElementById('modal-root') : null)
const getAppRoot = () => (typeof document !== 'undefined' ? document.body : null)


const Modal = ({
  isOpen = false,
  onCloseRequested,
  onOverlayClick,
  position = 'bottom',
  className,
  dark,
  children,
  // ActionSheetLayout props
  title,
  content,
  action,
  onButtonClick,
  buttonDisabled,
  showCloseButton = true,
  buttonLoading,
  hasHeightOverflow,
  style,
  footer,
  headerClassList,
  closeButtonClassName,
  closeButtonPosition = 'right',
  containerClass,
  // Sheet specific props
  detent = 'content-height',
  disableScrollLocking = true,
  sheetClassName = '',
  actionSheetClassName = '',
  /**
   * Mobile only: the sheet body becomes a scroll container. Drag-to-close only
   * takes over once it is scrolled back to the top.
   */
  scrollableContent = false,
  /** Override mobile sheet motion (login uses a shorter snap). */
  tweenConfig = DEFAULT_SHEET_TWEEN,
}) => {
  const contentRef = useRef()
  const isMobileSize = useIsMobileSize()
  const [isIOSKeyboardOpen, setIsIOSKeyboardOpen] = useState(false)
  // Keep the sheet mounted while isOpen flips false so exit animation can play.
  const [sheetMounted, setSheetMounted] = useState(isOpen)

  const onClose = useCallback(() => {
    if (onCloseRequested) onCloseRequested()
  }, [onCloseRequested])

  const handleOverlayClick = useCallback(
    event => {
      if (contentRef.current && contentRef.current.contains(event.target)) {
        return
      }
      if (onOverlayClick) {
        onOverlayClick()
      } else {
        onClose()
      }
    },
    [onOverlayClick, onClose]
  )

  const handleKeyUp = useCallback(
    event => {
      if (event.key === 'Escape') {
        onClose()
      }
    },
    [onClose]
  )

  const openModal = useCallback(() => {
    const appRoot = getAppRoot()
    if (!isMobileSize && appRoot) {
      appRoot.classList.add('app--noscroll')
      document.addEventListener('keyup', handleKeyUp)
    }
  }, [handleKeyUp, isMobileSize])

  const closeModal = useCallback(() => {
    const modalRoot = getModalRoot()
    const appRoot = getAppRoot()
    if (!isMobileSize && modalRoot && modalRoot.children.length <= 1 && appRoot) {
      appRoot.classList.remove('app--noscroll')
    }
    if (!isMobileSize) {
      document.removeEventListener('keyup', handleKeyUp)
    }
  }, [handleKeyUp, isMobileSize])

  useEffect(() => {
    if (isOpen) {
      setSheetMounted(true)
      openModal()
    } else {
      closeModal()
    }

    return () => {
      closeModal()
      const appRoot = getAppRoot()
      if (!isMobileSize && appRoot) appRoot.classList.remove('app--noscroll')
    }
  }, [isOpen, openModal, closeModal, isMobileSize])

  useEffect(() => {
    if (isOpen || !isMobileSize || !sheetMounted) return undefined
    const t = window.setTimeout(() => setSheetMounted(false), getSheetExitMs(tweenConfig))
    return () => window.clearTimeout(t)
  }, [isOpen, isMobileSize, sheetMounted, tweenConfig])

  useEffect(() => {
    const handleKeyboardOpen = () => setIsIOSKeyboardOpen(true)
    const handleKeyboardClose = () => setIsIOSKeyboardOpen(false)

    window.addEventListener('ios-keyboard-open', handleKeyboardOpen)
    window.addEventListener('ios-keyboard-close', handleKeyboardClose)

    return () => {
      window.removeEventListener('ios-keyboard-open', handleKeyboardOpen)
      window.removeEventListener('ios-keyboard-close', handleKeyboardClose)
    }
  }, [])

  const actionSheetProps = {
    className,
    containerClass,
    title,
    action,
    content: content || children,
    onButtonClick,
    buttonDisabled,
    showCloseButton,
    onClose,
    buttonLoading,
    hasHeightOverflow,
    style,
    footer,
    headerClassList,
    closeButtonClassName,
    closeButtonPosition,
    // A scrolling sheet needs native touch panning, which `touch-action: none`
    // on <body> would swallow.
    lockBodyScroll: !(scrollableContent && isMobileSize),
  }

  if (isMobileSize) {
    if (!isOpen && !sheetMounted) return null
    const baseTween = tweenConfig || DEFAULT_SHEET_TWEEN
    const sheetTween = isOpen ? baseTween : { ...baseTween, ease: SHEET_CLOSE_EASE }
    return (
      <Sheet
        isOpen={isOpen}
        onClose={onClose}
        detent={detent}
        disableScrollLocking={disableScrollLocking}
        tweenConfig={sheetTween}
        className={`!pointer-events-auto ${sheetClassName}`}>
        <Sheet.Container>
          <Sheet.Content className="bg-surface-1 rounded-t-2xl">
            {scrollableContent ? (
              <Sheet.Scroller draggableAt="top">
                <ActionSheetLayout {...actionSheetProps} actionSheetClassName={actionSheetClassName} />
              </Sheet.Scroller>
            ) : (
              <ActionSheetLayout {...actionSheetProps} actionSheetClassName={actionSheetClassName} />
            )}
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop onClick={onOverlayClick || onClose} className="cursor-pointer" />
      </Sheet>
    )
  }

  if (!isOpen) {
    return null
  }

  const modalClassList = classNames(className, 'fixed inset-0 flex justify-center', {
    'items-end': position === 'bottom',
    'items-center': position === 'full' || position === 'center',
    'md:!items-center': true,
    'z-[1000]': true,
    dark: dark,
    'ios-keyboard-active': isIOSKeyboardOpen && isMobileSize,
  })

  const backdropClassList = classNames(
    'modal-desktop-backdrop fixed inset-0 bg-black opacity-50 z-[999] transition-opacity',
    {
      'dark:bg-opacity-70': dark,
    }
  )

  const contentClassList = classNames('modal-desktop-content', {
    'w-full rounded-t-2xl shadow-lg': position === 'bottom',
    'w-full h-full': position === 'full',
    'w-full max-w-md rounded-2xl shadow-xl mx-4': position === 'center',
    'md:w-full md:max-w-lg md:rounded-2xl md:shadow-xl md:h-auto md:mx-0': true,
    'dark:bg-gray-900': dark,
  })

  const reactElement = (
    <>
      <div className={backdropClassList} aria-hidden="true" />
      <div className={modalClassList} onClick={handleOverlayClick}>
        <div className={contentClassList} ref={contentRef} onKeyUp={handleKeyUp}>
          <ActionSheetLayout {...actionSheetProps} actionSheetClassName={actionSheetClassName} />
        </div>
      </div>
    </>
  )

  const modalRoot = getModalRoot()
  if (!modalRoot) {
    return null
  }

  return ReactDOM.createPortal(reactElement, modalRoot)
}

export default React.memo(Modal)
